import { SKETCHES } from '../harness/registry';
import { CATEGORIES, type SketchMeta } from '../harness/Sketch';
import { HELP, FALLBACK } from '../harness/help';
import { renderScience } from './richtext';

// Inline icons (Material Symbols paths): a finger-tap and a science flask.
const ICON_TOUCH =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.46 13.99 3.5 11.5 3.5S7 5.46 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6C13 6.67 12.33 6 11.5 6S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.03-.24-.03-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.92-1.4z"/></svg>';
const ICON_SCIENCE =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M19.8 18.4 14 10.67V6.5l1.35-1.69c.26-.33.03-.81-.39-.81H9.04c-.42 0-.65.48-.39.81L10 6.5v4.17L4.2 18.4c-.49.66-.02 1.6.8 1.6h14c.82 0 1.29-.94.8-1.6zM7.6 18l3.27-4.36c.13-.17.13-.41 0-.58L7.6 9h8.8l-3.27 4.36c-.13.17-.13.41 0 .58L16.4 18H7.6z"/></svg>';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Grouped grid of sketch cards with a jump-nav. Tapping a card calls `onOpen(id)`. */
export class Menu {
  private readonly el: HTMLElement;
  private readonly modal: HTMLElement;
  private readonly modalTitle: HTMLElement;
  private readonly modalBody: HTMLElement;
  private readonly onOpen: (id: string) => void;
  private showSandbox = false;
  private readonly countEl: HTMLElement;
  private readonly pillsWrap: HTMLElement;
  private readonly callout: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly sectionsWrap: HTMLElement;

  constructor(root: HTMLElement, onOpen: (id: string) => void) {
    this.onOpen = onOpen;
    this.el = document.createElement('div');
    this.el.className = 'menu';

    const header = document.createElement('header');
    header.className = 'menu-header';
    header.innerHTML = `<h1>Mesmereyes</h1><p class="menu-count"></p>`;
    this.el.appendChild(header);
    this.countEl = header.querySelector('.menu-count')!;

    // sticky jump-nav: category pills, then the Sandbox toggle on the right
    const nav = document.createElement('nav');
    nav.className = 'menu-nav';
    this.pillsWrap = document.createElement('div');
    this.pillsWrap.className = 'nav-pills';
    nav.appendChild(this.pillsWrap);

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'sandbox-toggle';
    this.toggle.setAttribute('role', 'switch');
    this.toggle.setAttribute('aria-checked', 'false');
    this.toggle.innerHTML = `<span class="sandbox-knob"></span><span>Sandbox</span>`;
    this.toggle.addEventListener('click', () => this.setSandbox(!this.showSandbox));
    nav.appendChild(this.toggle);
    this.el.appendChild(nav);

    // call-out shown only while the Sandbox is revealed
    this.callout = document.createElement('div');
    this.callout.className = 'sandbox-callout hidden';
    this.callout.textContent =
      'Sandbox enabled: these are collected sketches that are interesting, but either in-progress, incomplete, or shelved.';
    this.el.appendChild(this.callout);

    this.sectionsWrap = document.createElement('div');
    this.el.appendChild(this.sectionsWrap);

    this.renderCatalogue();
    root.appendChild(this.el);

    // ── shared help modal (gestures / science) ──
    this.modal = document.createElement('div');
    this.modal.className = 'modal-backdrop hidden';
    this.modal.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true">
        <div class="modal-head"><h3></h3><button class="modal-close" type="button" aria-label="Close">&times;</button></div>
        <div class="modal-body"></div>
      </div>`;
    this.modalTitle = this.modal.querySelector('h3')!;
    this.modalBody = this.modal.querySelector('.modal-body')!;
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
    this.modal.querySelector('.modal-close')!.addEventListener('click', () => this.closeModal());
    root.appendChild(this.modal);
  }

  private setSandbox(on: boolean): void {
    this.showSandbox = on;
    this.toggle.classList.toggle('on', on);
    this.toggle.setAttribute('aria-checked', on ? 'true' : 'false');
    this.callout.classList.toggle('hidden', !on);
    this.renderCatalogue();
  }

  /** (Re)build the jump-nav pills and the category sections for the current
   *  filter — main catalogue only, or everything when the Sandbox is revealed. */
  private renderCatalogue(): void {
    const visible = SKETCHES.filter((s) => this.showSandbox || !s.sandbox);
    const present = CATEGORIES.filter((c) => visible.some((s) => s.category === c.id));
    this.countEl.textContent = `${visible.length} touch sketches — pick one`;

    this.pillsWrap.replaceChildren();
    for (const cat of present) {
      const pill = document.createElement('button');
      pill.className = 'nav-pill';
      pill.type = 'button';
      pill.textContent = cat.short;
      pill.style.setProperty('--cat', cat.accent);
      pill.addEventListener('click', () => {
        this.el.querySelector(`#cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      this.pillsWrap.appendChild(pill);
    }

    this.sectionsWrap.replaceChildren();
    for (const cat of present) {
      // alphabetical by title within each category section
      const items = visible.filter((s) => s.category === cat.id).sort((a, b) => a.title.localeCompare(b.title));
      const section = document.createElement('section');
      section.className = 'menu-section';
      section.id = `cat-${cat.id}`;
      section.style.setProperty('--cat', cat.accent);
      section.innerHTML =
        `<div class="section-head">` +
        `<h2>${cat.label}</h2><span class="section-blurb">${cat.blurb}</span>` +
        `<span class="section-count">${items.length}</span>` +
        `</div>`;
      const grid = document.createElement('div');
      grid.className = 'menu-grid';
      for (const s of items) grid.appendChild(this.buildCard(s));
      section.appendChild(grid);
      this.sectionsWrap.appendChild(section);
    }
  }

  private buildCard(s: SketchMeta): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';
    const needs = (s.needs ?? []).map((nd) => `<span class="need">${nd}</span>`).join('');
    const perf = (s.perf ?? []).map((p) => `<span class="warn">${p}</span>`).join('');
    const credit = s.credit
      ? ` <span class="card-credit">Inspired by <a href="${s.credit.url}" target="_blank" rel="noopener">${s.credit.author}</a></span>`
      : '';
    card.innerHTML = `
      <span class="card-thumb"${s.thumb ? ` style="background-image:url(${s.thumb})"` : ''}></span>
      <span class="card-title">${s.title}</span>
      <span class="card-blurb">${s.blurb}${credit}</span>
      <span class="card-tags">${perf}${needs}</span>
      <span class="card-bar">
        <button class="launch" type="button">Launch!</button>
        <button class="iconbtn" type="button" data-help="gestures" title="Touch controls" aria-label="Touch controls">${ICON_TOUCH}</button>
        <button class="iconbtn" type="button" data-help="science" title="The science" aria-label="The science">${ICON_SCIENCE}</button>
      </span>`;
    // launching: the Launch button or a tap on the card body (not the icons)
    card.querySelector('.launch')?.addEventListener('click', (e) => { e.stopPropagation(); this.onOpen(s.id); });
    card.addEventListener('click', () => this.onOpen(s.id));
    for (const btn of card.querySelectorAll<HTMLButtonElement>('.iconbtn')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openModal(s, btn.dataset.help as 'gestures' | 'science');
      });
    }
    // let the attribution link open without launching the sketch
    card.querySelector('.card-credit a')?.addEventListener('click', (e) => e.stopPropagation());
    return card;
  }

  private openModal(s: SketchMeta, kind: 'gestures' | 'science'): void {
    const help = HELP[s.id] ?? FALLBACK;
    if (kind === 'gestures') {
      this.modalTitle.textContent = `${s.title} — touch controls`;
      this.modalBody.innerHTML = help.gestures.length
        ? `<dl class="gestures">${help.gestures
            .map((g) => `<dt>${esc(g.g)}</dt><dd>${esc(g.e)}</dd>`)
            .join('')}</dl>`
        : `<p class="muted">This one plays on its own — no touch controls.</p>`;
    } else {
      this.modalTitle.textContent = `${s.title} — the science`;
      this.modalBody.innerHTML = `<div class="science">${renderScience(help.science || FALLBACK.science)}</div>`;
    }
    this.modalBody.scrollTop = 0;
    this.modal.classList.remove('hidden');
  }

  private closeModal(): void {
    this.modal.classList.add('hidden');
  }

  show(): void {
    this.el.classList.remove('hidden');
    this.el.scrollTop = 0;
  }

  hide(): void {
    this.el.classList.add('hidden');
  }
}
